import React, { useMemo } from "react";
import { PlainTooltip } from "@similarweb/ui-components";
import {
    BoxContainer,
    ChartContainer,
    ContentContainer,
    Disclaimer,
    LegendContainer,
    LegendDivider,
    LegendsLineLoaders,
    LegendsTitle,
    OverlapLegendWrapper,
    StyledHeaderTitle,
    SumOfLegendsContainer,
    TitleContainer,
    InfoIconContainer,
    TextBanner,
} from "./StyledComponents";
import { OverlapLoaders } from "pro-features/components/Loaders/src/OverlapVennLoader";
import { FlexColumn, FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import BoxTitle, { InfoIcon } from "pro-features/components/BoxTitle/src/BoxTitle";
import { i18nFilter } from "app/filters/i18nFilter";
import { minVisitsAbbrFilter } from "app/filters/ngFilters";
import Chart from "pro-features/components/Chart/src/Chart";
import { getChartConfig } from "./chartConfig";
import { Legends } from "app/components/React/Legends/Legends";
import { LegendWithOneLineBulletFlex } from "@similarweb/ui-components";
import { TableNoData } from "app/components/React/Table/FlexTable/Big/FlexTableStatelessComponents";
import { PixelPlaceholderLoader } from "@similarweb/ui-components";
import { SWReactIcons } from "@similarweb/icons";
import { SharedVisitorsTable } from "app/pages/website-analysis/audience-overlap/SharedVisitorsTable/SharedVisitorsTable";
import type { IVennChartData } from "app/pages/website-analysis/audience-overlap/types";
import { WidgetSubtitle } from "app/components/React/Widgets/WidgetsSubtitle";
import { allTrackers } from "app/services/track/track";
import type { ISiteLegendItem } from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelAnalysisChart/ChannelAnalysisChartContainer";
import { colorsUtilities } from "app/UtilitiesAndConstants/UtilityFunctions/colorsUtilities";

const getSetsOpacity = (set: string[], chosenSitesLegends: ISiteLegendItem[], isSingleMode: boolean) => {
    // In case we're in comparison mode - then all parts of the graph should have maximum opacity
    // (all of the sites were chosen by the user)
    if (!isSingleMode) {
        return 1;
    }

    const userChosenSite = chosenSitesLegends[0];

    // We want our opacity to be 100% in case the set consists of user selected site only
    const isFullyChosen = set.every((siteName) => siteName === userChosenSite.name);
    if (isFullyChosen) {
        return 1;
    }

    // Otherwise - we want our opacity to be 40% if all sites in the set are auto suggestions,
    // and 70% if the set consists of a mix between user selected and auto suggestions.
    const isPartiallyChosen = set.some((siteName) => siteName === userChosenSite.name);
    return isPartiallyChosen ? 0.7 : 0.4;
};

const getSetsColor = (chosenItems) => (setsArray) => {
    if (setsArray.length === 1) {
        return chosenItems.find(({ name, Domain }) => name === setsArray[0] || Domain === setsArray[0])?.color;
    }
    return colorsUtilities.multiplyHexColors(
        getSetsColor(chosenItems)(setsArray.slice(0, 1)),
        getSetsColor(chosenItems)(setsArray.slice(1)),
    );
};

const LEGENDS_LOADER_WIDTH = 248;
const LEGENDS_LOADER_HEIGHT = 16;
const MINIMUM_OVERLAP_SHARE_THRESHOLD = 0.03;
const MINIMUM_OVERLAP_SHARE_FROW_LARGEST_THRESHOLD = 0.002;
const VENN_NORMALIZE_FACTOR = 4; // Decrease for blow up intersection visibility

interface IVennDiagramComponentProps {
    chosenSitesLegends?: any;
    isSingleMode?: boolean;
    websitesArrayWithColor?: any[];
    VennDiagramData?: any;
    isVennDiagramLoading?: boolean;
    selectedCountryCode?: string;
    excelDownloadUrl?: string;
}

export const VennDiagramComponent: React.FC<IVennDiagramComponentProps> = (props) => {
    const {
        VennDiagramData,
        isVennDiagramLoading,
        chosenSitesLegends,
        isSingleMode,
        websitesArrayWithColor,
        selectedCountryCode,
        excelDownloadUrl,
    } = props;
    const { i18n } = React.useMemo(
        () => ({
            i18n: i18nFilter(),
        }),
        [],
    );
    const chartRef = React.useRef<HTMLDivElement>(null);

    const sitesOrder = useMemo(() => {
        return isSingleMode
            ? websitesArrayWithColor?.map((site) => site.name)
            : chosenSitesLegends?.map((site) => site.name);
    }, [isSingleMode, websitesArrayWithColor, chosenSitesLegends]);

    const chartData: IVennChartData = React.useMemo((): IVennChartData => {
        if (isVennDiagramLoading) {
            return;
        }
        const sitesEntries: any = Object.entries(VennDiagramData.data?.OverlapData?.Data ?? {});
        const sitesValues: Array<number> = sitesEntries.map(([set, value]) => value);
        const sortedSitesVal: Array<number> = sitesValues.sort((a: number, b: number) => b - a);
        const smallestSiteVal = sortedSitesVal[sitesValues.length - 1];
        const biggestSiteVal = sortedSitesVal[0];

        return sitesEntries.map(([set, value]: any, idx) => {
            const setArray = set.split(",");

            return {
                sets: setArray,
                //We normalize the graph by increasing the value relatively (by degree of intersections in sets)
                value:
                    sitesOrder.length < 4 ||
                    value / smallestSiteVal < MINIMUM_OVERLAP_SHARE_THRESHOLD ||
                    value / biggestSiteVal < MINIMUM_OVERLAP_SHARE_FROW_LARGEST_THRESHOLD
                        ? value
                        : value + biggestSiteVal / VENN_NORMALIZE_FACTOR,
                realValue: value,
                color: isSingleMode
                    ? getSetsColor(websitesArrayWithColor)(setArray)
                    : getSetsColor(chosenSitesLegends)(setArray),
                dataLabels: {
                    enabled: false,
                },
                opacity: getSetsOpacity(setArray, chosenSitesLegends, isSingleMode),
            };
        });
    }, [
        isVennDiagramLoading,
        VennDiagramData.data,
        chosenSitesLegends,
        sitesOrder,
        isSingleMode,
        websitesArrayWithColor,
    ]);

    const legendsItems = React.useMemo(() => {
        if (props.isSingleMode) {
            return websitesArrayWithColor?.map((website) => {
                return {
                    name: website.Domain,
                    color: website.color,
                    setOpacity: website.isSuggested,
                    isDisabled: true,
                    gridRowGap: "14px",
                    data: minVisitsAbbrFilter()(
                        chartData?.filter(
                            (permutation) => permutation.sets.length === 1 && permutation.sets[0] === website.Domain,
                        )[0]?.realValue,
                    ),
                };
            });
        }
        return chosenSitesLegends?.map((website) => {
            return {
                name: website.name,
                color: website.color,
                setOpacity: false,
                isDisabled: true,
                gridRowGap: "14px",
                data: minVisitsAbbrFilter()(
                    chartData?.filter(
                        (permutation) => permutation.sets.length === 1 && permutation.sets[0] === website.name,
                    )[0]?.realValue,
                ),
            };
        });
    }, [chartData, websitesArrayWithColor, chosenSitesLegends, props.isSingleMode]);

    const onDownloadClick = () => {
        allTrackers.dispatchTrackEvent(
            "website.analysis.audience.overlap.download.excel",
            "click",
            "website.analysis.audience.overlap.download.excel",
        );
    };

    const renderLoaders = () => {
        return (
            <ContentContainer>
                <OverlapLoaders />
                <LegendContainer>
                    <PixelPlaceholderLoader
                        width={99}
                        height={LEGENDS_LOADER_HEIGHT}
                    />
                    <LegendsLineLoaders>
                        <PixelPlaceholderLoader
                            width={LEGENDS_LOADER_WIDTH}
                            height={LEGENDS_LOADER_HEIGHT}
                        />
                        <PixelPlaceholderLoader
                            width={LEGENDS_LOADER_WIDTH}
                            height={LEGENDS_LOADER_HEIGHT}
                        />
                        <PixelPlaceholderLoader
                            width={LEGENDS_LOADER_WIDTH}
                            height={LEGENDS_LOADER_HEIGHT}
                        />
                    </LegendsLineLoaders>
                    <LegendDivider />
                    <LegendsLineLoaders>
                        <PixelPlaceholderLoader
                            width={LEGENDS_LOADER_WIDTH}
                            height={LEGENDS_LOADER_HEIGHT}
                        />
                    </LegendsLineLoaders>
                </LegendContainer>
            </ContentContainer>
        );
    };
    return (
        <BoxContainer ref={chartRef}>
            <TitleContainer>
                <FlexColumn>
                    <StyledHeaderTitle>
                        <BoxTitle tooltip={i18n("analysis.audience.overlap.competitive.title.tooltip")}>
                            {i18n("analysis.audience.overlap.competitive.title")}
                        </BoxTitle>
                    </StyledHeaderTitle>
                    <WidgetSubtitle />
                </FlexColumn>
            </TitleContainer>
            {isVennDiagramLoading ? renderLoaders() : null}
            {!isVennDiagramLoading ? (
                <div>
                    {sitesOrder && sitesOrder.length > 2 ? (
                        <Disclaimer>
                            <SWReactIcons
                                iconName="info"
                                size="xs"
                            />
                            <TextBanner>
                                {i18nFilter()("analysis.audience.overlap.more.than.three.comparisons.discalimer")}
                            </TextBanner>
                        </Disclaimer>
                    ) : null}
                    {VennDiagramData?.data?.OverlapData?.Data ? (
                        <ContentContainer>
                            <ChartContainer>
                                <Chart
                                    type={"venn"}
                                    data={chartData}
                                    config={getChartConfig(chartData, sitesOrder)}
                                />
                            </ChartContainer>

                            <LegendContainer>
                                <LegendsTitle>{i18nFilter()("analysis.audience.overlap.legend.title")}</LegendsTitle>
                                <Legends
                                    toggleSeries={() => undefined}
                                    legendComponentWrapper={OverlapLegendWrapper}
                                    legendComponent={LegendWithOneLineBulletFlex}
                                    legendItems={legendsItems}
                                    gridDirection="column"
                                />
                                <LegendDivider />
                                <SumOfLegendsContainer>
                                    <FlexRow>
                                        {i18nFilter()("analysis.audience.overlap.legend.deduplicated")}
                                        <PlainTooltip
                                            placement="top"
                                            tooltipContent={i18nFilter()(
                                                "analysis.audience.overlap.legend.deduplicated.tooltip",
                                            )}
                                        >
                                            <InfoIconContainer>
                                                <InfoIcon iconName="info" />
                                            </InfoIconContainer>
                                        </PlainTooltip>
                                    </FlexRow>
                                    <div>{minVisitsAbbrFilter()(VennDiagramData.data?.OverlapData.Total)}</div>
                                </SumOfLegendsContainer>
                            </LegendContainer>
                        </ContentContainer>
                    ) : (
                        <TableNoData icon="no-data" />
                    )}
                </div>
            ) : null}
            <div
                style={{
                    width: "100%",
                    backgroundColor: "white",
                    paddingTop: "20px",
                }}
            >
                <SharedVisitorsTable
                    vennChartData={chartData}
                    rawData={VennDiagramData.data?.OverlapData}
                    chosenSitesLegends={chosenSitesLegends}
                    isLoading={isVennDiagramLoading}
                    excelLink={excelDownloadUrl}
                    vennDiagramChart={chartRef.current}
                    isSingleMode={isSingleMode}
                />
            </div>
        </BoxContainer>
    );
};
